import { create } from "zustand"
import type { TournamentSummary } from "@/shared/api/types/tournaments"

/**
 * Drives the tournament welcome modal (a global overlay mounted in `App`).
 * Decoupled from `useTournamentStore` so the modal can be previewed with a
 * mock tournament (emulator) without touching live tournament state.
 *
 * Auto-open (once per unseen tournament) is handled by the modal component
 * watching `useTournamentStore`; `show` is the imperative entry point used by
 * both that controller and the emulator preview.
 */
export type TournamentWelcomeOpenSource = "auto" | "manual"

interface TournamentWelcomeState {
  /** Tournament currently displayed in the modal, or null when closed. */
  tournament: TournamentSummary | null
  isOpen: boolean
  openSource: TournamentWelcomeOpenSource | null
  show: (
    tournament: TournamentSummary,
    options?: { open_source?: TournamentWelcomeOpenSource }
  ) => void
  close: () => void
}

export const useTournamentWelcomeStore = create<TournamentWelcomeState>(
  (set) => ({
    tournament: null,
    isOpen: false,
    openSource: null,
    show: (tournament, options) =>
      set({
        tournament,
        isOpen: true,
        openSource: options?.open_source ?? "manual",
      }),
    close: () => set({ isOpen: false }),
  })
)
